import React from "react";

// Single source of truth for value -> colour. ONLY editable status/stage fields are colour-coded;
// every other field renders as plain text. Each status field uses a small, distinct palette,
// with exactly one green so shades never blur together.
const STATUS_COLORS = {
  // green = good / current / final-positive (one per field)
  up_to_date: "green", completed: "green", delivered: "green", won: "green", on_lot: "green", ok: "green", done: "green", operational: "green", low: "green",
  // teal = staged / contacted / delivery-scheduled
  contacted: "teal", ready_for_pdi: "teal", delivery_scheduled: "teal", complete: "teal",
  // blue = active / working / new
  working: "blue", in_progress: "blue", inspection: "blue", new: "blue", picked_up: "blue", in_transit: "blue",
  // purple = scheduled / on hold
  scheduled: "purple", waiting_parts: "purple", in_north_shop: "purple",
  // orange = service / hand-off
  service: "orange", ready_for_acr: "orange", waiting_repair: "orange", pickup_scheduled: "orange",
  // yellow = waiting / attention
  not_started: "yellow", due_for: "yellow", due_soon: "yellow", quoted: "yellow", queued: "yellow", open: "yellow", attention: "yellow", medium: "yellow",
  // red = urgent / problem
  overdue: "red", down: "red", blocked: "red", delayed: "red", high: "red",
  repair: "darkred",
  // navy = closed / in-shop / sold
  in_shop: "navy", sold: "navy",
  // cyan = transferred
  transferred: "cyan",
  // slate = inactive / dropped
  lost: "slate", not_contacted: "slate",
};

const COLOR_CLASSES = {
  green: "bg-[#00C853] text-white",
  dgreen: "bg-[#00913C] text-white",
  red: "bg-[#D50000] text-white",
  yellow: "bg-[#FFD600] text-black",
  blue: "bg-[#2962FF] text-white",
  slate: "bg-slate-700 text-white",
  navy: "bg-[#0D1B5E] text-white",
  orange: "bg-[#C24E00] text-white",
  darkred: "bg-[#7A0010] text-white",
  purple: "bg-[#7B1FA2] text-white",
  gray: "bg-gray-200 text-gray-500",
  teal: "bg-[#0D9488] text-white",
  lightblue: "bg-[#38BDF8] text-white",
  indigo: "bg-[#3949AB] text-white",
  cyan: "bg-[#00ACC1] text-white",
  lime: "bg-[#558B2F] text-white",
  pink: "bg-[#D81B60] text-white",
  brown: "bg-[#6D4C41] text-white",
  magenta: "bg-[#AD1457] text-white",
  amber: "bg-[#FF8F00] text-black",
};

const DOT_CLASSES = {
  green: "bg-[#00C853]", dgreen: "bg-[#00913C]", red: "bg-[#D50000]", yellow: "bg-[#FFD600]",
  blue: "bg-[#2962FF]", slate: "bg-slate-500", purple: "bg-[#7B1FA2]",
  navy: "bg-[#0D1B5E]", orange: "bg-[#C24E00]", darkred: "bg-[#7A0010]", teal: "bg-[#0D9488]", lightblue: "bg-[#38BDF8]",
  indigo: "bg-[#3949AB]", cyan: "bg-[#00ACC1]", lime: "bg-[#558B2F]", pink: "bg-[#D81B60]", brown: "bg-[#6D4C41]", magenta: "bg-[#AD1457]", amber: "bg-[#FF8F00]",
};

export const labelize = (s) =>
  (s || "").toString().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_LABELS = { ready_for_pdi: "Ready for PDI", ready_for_acr: "Ready for ACR", waiting_repair: "Waiting on Repair", up_to_date: "Up to Date", due_for: "Due For" };
export const displayLabel = (s) => STATUS_LABELS[s] || labelize(s);

export const statusColor = (status) => STATUS_COLORS[status] || "slate";

export const StatusBadge = ({ status }) => {
  const color = statusColor(status);
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES[color]}`}
    >
      {displayLabel(status)}
    </span>
  );
};

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY_COLOR = { 1: "blue", 2: "green", 3: "orange", 4: "purple", 5: "navy", 0: "slate", 6: "slate" };

export const parseYMD = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((s || "").trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const back = (x.getDay() + 6) % 7; // Monday = start of week
  x.setDate(x.getDate() - back);
  return x;
};

// Given a scheduled date, return its label + color. Current-week jobs get their
// weekday color; jobs outside this week are grayed out until their week arrives.
export const scheduleInfo = (dateStr) => {
  const d = parseYMD(dateStr);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekStart = startOfWeek(today);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const inThisWeek = d >= weekStart && d <= weekEnd;
  const color = inThisWeek ? WEEKDAY_COLOR[d.getDay()] : "gray";
  const label = `${WD[d.getDay()]} ${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  return { date: d, inThisWeek, color, label };
};

// Shared date display: "Jul 8, 2026" (month, day, year). Stored values stay ISO.
export const fmtDate = (s) => {
  const d = parseYMD(s);
  return d ? `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}` : "";
};

export const ScheduleBadge = ({ date }) => {
  const info = scheduleInfo(date);
  if (!info) return <span className="text-gray-300">—</span>;
  return (
    <span
      data-testid="schedule-badge"
      className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES[info.color]}`}
    >
      {info.label}
    </span>
  );
};

export const StatusDot = ({ status }) => (
  <span className={`inline-block w-3 h-3 rounded-full ${DOT_CLASSES[statusColor(status)]}`} />
);

export const addMonths = (dateStr, months) => {
  const d = parseYMD(dateStr);
  if (!d || !months) return null;
  const x = new Date(d);
  x.setMonth(x.getMonth() + Number(months));
  return x;
};

// For a recurring Facilities task: next due = last_done + repeat_months.
export const nextDueInfo = (row) => {
  const months = Number(row.repeat_months) || 0;
  if (!months || !row.last_done) return null;
  const due = addMonths(row.last_done, months);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / 86400000);
  const urgency = diffDays < 0 ? "overdue" : diffDays <= 14 ? "due_soon" : "ok";
  return { due, urgency, diffDays, label: `${MO[due.getMonth()]} ${due.getDate()}, ${due.getFullYear()}` };
};

export const DueBadge = ({ row }) => {
  const info = nextDueInfo(row);
  if (!info) return <span className="text-gray-300">—</span>;
  if (info.urgency === "overdue")
    return <span data-testid="due-badge" className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES.red}`}>Due · {info.label}</span>;
  if (info.urgency === "due_soon")
    return <span data-testid="due-badge" className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES.yellow}`}>Due Soon · {info.label}</span>;
  return <span className="text-sm text-gray-600">{info.label}</span>;
};

// For a fixed due date (e.g. inspection). Flags "Due Soon" within leadDays, "Overdue" once past.
export const dateDueInfo = (dateStr, leadDays = 30) => {
  const d = parseYMD(dateStr);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - today) / 86400000);
  const urgency = diffDays < 0 ? "overdue" : diffDays <= leadDays ? "due_soon" : "ok";
  return { date: d, urgency, diffDays, label: fmtDate(dateStr) };
};

export const InspectionBadge = ({ date, leadDays = 30 }) => {
  const info = dateDueInfo(date, leadDays);
  if (!info) return <span className="text-gray-300">—</span>;
  if (info.urgency === "overdue")
    return <span data-testid="insp-badge" className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES.red}`}>Overdue · {info.label}</span>;
  if (info.urgency === "due_soon")
    return <span data-testid="insp-badge" className={`inline-block text-xs lg:text-sm font-bold uppercase tracking-wide px-3 py-1 ${COLOR_CLASSES.yellow}`}>Due Soon · {info.label}</span>;
  return <span className="text-sm text-gray-600">{info.label}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 border-b-4 border-gray-900 pb-5 mb-8">
    <div>
      <h1 data-testid="page-title" className="font-head text-4xl lg:text-5xl font-black uppercase tracking-tighter text-gray-900">
        {title}
      </h1>
      {subtitle && <p className="text-base lg:text-lg text-gray-500 mt-1">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">{actions}</div>
  </div>
);

export const KpiCard = ({ label, value, status, testId }) => (
  <div data-testid={testId} className="bg-white border-2 border-gray-200 p-6 lg:p-8 hover:border-gray-900 transition-colors duration-200">
    <div className="flex items-center justify-between">
      <span className="text-xs lg:text-sm uppercase tracking-widest font-bold text-gray-500">{label}</span>
      {status && <StatusDot status={status} />}
    </div>
    <div className="font-mono-data text-5xl lg:text-6xl font-light tracking-tighter mt-4 text-gray-900">{value}</div>
  </div>
);
